import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.jsx';
import { api } from '../lib/api.js';
import { useToast, Field } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';
import AuthShell, { AuthHead } from '../components/AuthShell.jsx';
import DocumentUpload from '../components/DocumentUpload.jsx';
import LocationFields from '../components/LocationFields.jsx';
import LanguageSelect from '../components/LanguageSelect.jsx';
import PhoneField from '../components/PhoneField.jsx';
import SearchableMultiSelect from '../components/SearchableMultiSelect.jsx';
import ServiceIcon from '../components/ServiceIcon.jsx';
import { isValidPhone } from '../lib/validation.js';
import { ALL_CITIES } from '../lib/locations.js';

const STEPS = [
  { name: 'Your account', icon: 'user' },
  { name: 'Professional', icon: 'stethoscope' },
  { name: 'Documents', icon: 'file-check' },
];

export default function RegisterNurse() {
  const { register } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [serviceIds, setServiceIds] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    lat: null,
    lng: null,
    headline: '',
    bio: '',
    experienceYears: '',
    hourlyRate: '',
    languages: '',
    qualifications: '',
    certifications: '',
    serviceAreas: '',
    licenseNumber: '',
  });

  useEffect(() => {
    api.get('/api/services').then(setServices).catch(() => {});
    api.get('/api/document-kinds').then((d) => setKinds(d.kinds)).catch(() => setKinds([]));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleService = (id) =>
    setServiceIds((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const requiredKinds = kinds.filter((k) => k.required);
  const missing = requiredKinds.filter((k) => !files[k.key]).map((k) => k.label);

  const valid = [
    form.name.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
      form.password.length >= 6 &&
      isValidPhone(form.phone) &&
      form.gender &&
      form.state &&
      form.city &&
      form.lat != null,
    form.experienceYears !== '' &&
      form.hourlyRate !== '' &&
      form.licenseNumber.trim() &&
      form.qualifications.trim() &&
      form.languages.trim() &&
      form.serviceAreas.trim() &&
      serviceIds.length > 0,
    missing.length === 0,
  ];

  const submit = async () => {
    setBusy('submit');
    setError('');
    try {
      // The account has to exist before files can be attached to it, so the
      // application is created first and the uploads follow on its token.
      await register({ ...form, role: 'nurse', serviceIds });

      const entries = Object.entries(files);
      for (const [kind, file] of entries) {
        setBusy(`Uploading ${file.name}…`);
        await api.upload('/api/documents', file, { kind, filename: file.name });
      }

      toast.success('Application submitted — an administrator will review your documents.');
      navigate('/nurse', { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy('');
      // If the account was created but an upload failed, the nurse is signed in
      // already; send them to the profile page to finish uploading.
      if (/upload/i.test(err.message)) {
        toast.error('Account created, but a document did not upload. Finish from your profile.');
        navigate('/nurse/profile', { replace: true });
      }
    }
  };

  const next = () => {
    if (step < 2) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else submit();
  };

  return (
    <AuthShell back={{ to: '/register' }} wide>
      <AuthHead title="Apply as a home nurse" subtitle={`Step ${step + 1} of 3 · ${STEPS[step].name}`} />

      <nav className="wizard-rail" aria-label="Application steps">
        {STEPS.map((s, i) => (
          <button
            key={s.name}
            type="button"
            data-state={i === step ? 'current' : i < step ? 'done' : 'todo'}
            disabled={i > step && !valid.slice(0, i).every(Boolean)}
            onClick={() => i <= step && setStep(i)}
            aria-current={i === step ? 'step' : undefined}
          >
            <span className="wizard-bar" />
            <span className="wizard-name">
              {i + 1}. {s.name}
            </span>
          </button>
        ))}
      </nav>

      {error && (
        <div className="banner banner-danger">
          <Icon name="warning" />
          <span className="grow">{error}</span>
        </div>
      )}

      <div className="step-panel" key={step}>
        {step === 0 && (
          <>
            <div className="form-section">
              <span className="form-section-num">1</span>
              <h2>Your account</h2>
            </div>

            <div className="auth-form-grid">
              <Field label="Full name" id="n-name" required>
                <input id="n-name" type="text" value={form.name} onChange={set('name')} required autoComplete="name" placeholder="Sr. Lakshmi Pillai" />
              </Field>
              <Field label="Email address" id="n-email" required>
                <input id="n-email" type="email" value={form.email} onChange={set('email')} required autoComplete="email" placeholder="you@example.com" />
              </Field>
              <Field label="Password" id="n-pw" hint="At least 6 characters." required>
                <div className="input-icon">
                  <Icon name="lock" />
                  <input
                    id="n-pw"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={set('password')}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    style={{ paddingRight: 46 }}
                  />
                  <button
                    type="button"
                    className="input-clear"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <Icon name="eye" />
                  </button>
                </div>
              </Field>
              <PhoneField id="n-phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} required />
              <Field label="Gender" id="n-gender" hint="Patients can filter by this." required>
                <select id="n-gender" value={form.gender} onChange={set('gender')} required>
                  <option value="">Select…</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>

            <div className="form-section">
              <span className="form-section-num">2</span>
              <h2>Where you are based</h2>
            </div>
            <LocationFields
              value={{ state: form.state, city: form.city, address: form.address, lat: form.lat, lng: form.lng }}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />
          </>
        )}

        {step === 1 && (
          <>
            <div className="form-section">
              <span className="form-section-num">1</span>
              <h2>How you present to patients</h2>
            </div>

            <div className="auth-form-grid">
              <Field label="Professional headline" id="n-headline" hint="The first line patients read." className="span-all">
                <input
                  id="n-headline"
                  type="text"
                  value={form.headline}
                  onChange={set('headline')}
                  placeholder="Post-operative care nurse · 6 years"
                />
              </Field>
              <Field label="Years of experience" id="n-exp" required>
                <input id="n-exp" type="number" min="0" max="60" value={form.experienceYears} onChange={set('experienceYears')} required />
              </Field>
              <Field label="Hourly rate (₹)" id="n-rate" required>
                <input id="n-rate" type="number" min="0" step="10" value={form.hourlyRate} onChange={set('hourlyRate')} required />
              </Field>
            </div>

            <div className="form-section">
              <span className="form-section-num">2</span>
              <h2>Credentials</h2>
            </div>

            <div className="auth-form-grid">
              <Field label="Nursing council licence number" id="n-lic" required>
                <input id="n-lic" type="text" value={form.licenseNumber} onChange={set('licenseNumber')} required />
              </Field>
              <Field label="Qualifications" id="n-qual" required>
                <input id="n-qual" type="text" value={form.qualifications} onChange={set('qualifications')} placeholder="BSc Nursing, …" required />
              </Field>
              <Field label="Certifications" id="n-cert" hint="Comma separated." optional>
                <input id="n-cert" type="text" value={form.certifications} onChange={set('certifications')} placeholder="BLS, ACLS" />
              </Field>
            </div>

            <div className="form-section">
              <span className="form-section-num">3</span>
              <h2>Reach &amp; services</h2>
            </div>

            <div className="auth-form-grid">
              <Field label="Languages spoken" id="n-lang" hint="Choose every language you speak." required>
                <LanguageSelect id="n-lang" value={form.languages} onChange={(v) => setForm((f) => ({ ...f, languages: v }))} />
              </Field>
              <Field
                label="Service areas"
                id="n-areas"
                hint="Cities you travel to for visits — used to match you with nearby patients."
                required
              >
                <SearchableMultiSelect
                  id="n-areas"
                  options={ALL_CITIES}
                  value={form.serviceAreas}
                  onChange={(v) => setForm((f) => ({ ...f, serviceAreas: v }))}
                  placeholder="Search a city to add…"
                  emptyText="No matching city"
                />
              </Field>
              <Field label="Services you provide" required className="span-all">
                <div className="chips">
                  {services.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="chip"
                      aria-pressed={serviceIds.includes(s.id)}
                      onClick={() => toggleService(s.id)}
                    >
                      <ServiceIcon service={s} size={16} /> {s.name}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="About you" id="n-bio" optional className="span-all">
                <textarea id="n-bio" value={form.bio} onChange={set('bio')} placeholder="Describe your specialities and the care you provide." />
              </Field>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="banner banner-info">
              <Icon name="lock" />
              <span className="grow">
                An administrator checks each document before your profile is shown to patients. Files are
                private — only you and the reviewing administrator can open them.
              </span>
            </div>

            {/* Two credential slots per row on a wide card — the list is five
                items long and reads as a wall in one column. */}
            <div className="auth-form-grid">
              {kinds.map((k) => (
                <DocumentUpload
                  key={k.key}
                  kind={k.key}
                  label={k.label}
                  hint={k.hint}
                  required={k.required}
                  file={files[k.key]}
                  onPick={(kind, file) => setFiles((f) => ({ ...f, [kind]: file }))}
                  onClear={(kind) =>
                    setFiles((f) => {
                      const next = { ...f };
                      delete next[kind];
                      return next;
                    })
                  }
                />
              ))}
            </div>

            {missing.length > 0 && (
              <p className="tiny dim center mt-1">Still needed: {missing.join(', ')}.</p>
            )}
          </>
        )}
      </div>

      {/* The actions stay a readable width even though the form is wide. */}
      <div className="row mt-3" style={{ maxWidth: 460, marginInline: 'auto' }}>
        {step > 0 && (
          <button type="button" className="btn" onClick={() => setStep(step - 1)} disabled={!!busy}>
            <Icon name="arrow-left" /> Back
          </button>
        )}
        <button className="btn btn-primary btn-lg grow" onClick={next} disabled={!valid[step] || !!busy}>
          {busy ? (
            <>
              <span className="spinner" /> {busy !== 'submit' && busy}
            </>
          ) : step < 2 ? (
            <>
              Continue <Icon name="arrow-right" />
            </>
          ) : (
            'Submit application'
          )}
        </button>
      </div>

      <p className="auth-foot">
        Looking for care instead? <Link to="/register/patient">Sign up as a patient</Link>
      </p>
    </AuthShell>
  );
}
